import React from 'react';
import {
    Route,
    Redirect,
    RouteComponentProps,
    RouteProps,
} from 'react-router-dom';
import { Location } from 'history';
import { logOut, lastPageVisitedKey } from './authHelpers';
import routes from './commonVariables/routes';
import { useAuth } from '../contexts/AuthContext';
import {
    AppSections,
    AppSectionsCore,
    AppSectionsProfile,
    AppSectionsZinantis,
    AppSectionsZitoneo,
} from '../types/appConfiguration/appConfigTypes';
import { useAppConfiguration } from '../contexts/AppConfigurationContext';

type VisibilityProps =
    | {
          section: AppSections;
          page:
              | AppSectionsCore
              | AppSectionsZinantis
              | AppSectionsProfile
              | AppSectionsZitoneo;
      }
    | { section: AppSections; page?: never } // only section visibility
    | { alwaysVisible: true };

interface ProtectedRouteProps extends RouteProps {
    path: string;
    component?: React.ComponentType<RouteComponentProps>;
    render?: (props: RouteComponentProps) => React.ReactNode;
    actionWhenNotLoggedIn?: () => React.ReactElement;
    location?: Location;
}

type ProtectedRoutePropsWithVisibility = ProtectedRouteProps & VisibilityProps;

const ProtectedRoute = ({
    path,
    component: Component,
    render,
    actionWhenNotLoggedIn,
    ...rest
}: ProtectedRoutePropsWithVisibility) => {
    const { isAuthenticated } = useAuth();
    const { appConfiguration } = useAppConfiguration();

    const defaultActionWhenNotLoggedIn = (): React.ReactElement => {
        logOut();
        return <Redirect to={routes.login} />;
    };

    const resolvedAction =
        actionWhenNotLoggedIn ?? defaultActionWhenNotLoggedIn;

    localStorage.setItem(
        lastPageVisitedKey,
        `${rest.location?.pathname}${rest.location?.search}`
    );

    const getIsRouteVisible = (): boolean => {
        if ('alwaysVisible' in rest) return true;
        const { section, page } = rest as {
            section: AppSections;
            page:
                | AppSectionsCore
                | AppSectionsZinantis
                | AppSectionsProfile
                | AppSectionsZitoneo;
        };
        const sectionConfig = appConfiguration.sections[section];
        if (!sectionConfig.isVisible) return false;
        return (sectionConfig.pages as Record<string, boolean>)[page] ?? false;
    };

    const isRouteVisible = getIsRouteVisible();

    return (
        <Route
            {...rest}
            path={path}
            render={(props: RouteComponentProps) => {
                if (isAuthenticated === null) return null;
                if (!isAuthenticated) return resolvedAction();
                if (!isRouteVisible)
                    return <Redirect to={routes.landingPrivate} />;
                return Component ? <Component {...props} /> : render?.(props);
            }}
        />
    );
};

export default ProtectedRoute;
